import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

interface TransferDetectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: any;
  potentialTransfers: any[];
  onConfirmTransfer: (transfer: { id: number; linked_account_id: number }) => void;
}

export const TransferDetectionModal: React.FC<TransferDetectionModalProps> = ({
  isOpen,
  onClose,
  transaction,
  potentialTransfers,
  onConfirmTransfer
}) => {
  const [selectedTransfer, setSelectedTransfer] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSelectedTransfer(null);
  }, [transaction?.id]);

  const handleConfirm = async () => {
    if (!selectedTransfer) return;
    const selected = potentialTransfers.find(t => t.id === selectedTransfer);
    if (!selected) return;

    setLoading(true);
    try {
      await onConfirmTransfer({ id: selected.id, linked_account_id: selected.linked_account_id });
      onClose();
    } catch (error) {
      console.error('Error confirming transfer:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDaysDifference = (date1: string, date2: string) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) { setSelectedTransfer(null); onClose(); } }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-dialog-content border border-border shadow-2xl">
        <DialogHeader>
          <DialogTitle>Transfer Detection</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Source transaction */}
          <div>
            <h3 className="text-[var(--text-secondary)] text-sm font-medium mb-3 uppercase tracking-wide">
              Source Transaction
            </h3>
            <div className="rounded-lg border border-[var(--border-color)] bg-[var(--background-dark)] p-4">
              <div className="flex items-center gap-4">
                <div className="text-2xl font-bold text-[var(--primary-color)] min-w-[120px]">
                  {formatCurrency(transaction?.amount)}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-[var(--text-primary)] mb-1">
                    {transaction?.merchant_name || transaction?.description}
                  </div>
                  <div className="flex gap-4 text-sm text-[var(--text-secondary)]">
                    <span>{transaction?.account_name}</span>
                    <span>{transaction && formatDate(transaction.date)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Candidate matches */}
          <div>
            <h3 className="text-[var(--text-secondary)] text-sm font-medium mb-3 uppercase tracking-wide">
              Potential Transfer Matches
            </h3>
            {potentialTransfers.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-secondary)]">
                <p className="mb-2">No potential transfer matches found.</p>
                <p>This might not be a transfer, or the matching transaction hasn't been imported yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {potentialTransfers.map((transfer) => {
                  const isSelected = selectedTransfer === transfer.id;
                  const days = getDaysDifference(transaction.date, transfer.date);
                  const pct = Math.round(
                    (1 - Math.abs(Math.abs(transfer.amount) - Math.abs(transaction.amount)) / Math.abs(transaction.amount)) * 100
                  );
                  return (
                    <div
                      key={transfer.id}
                      className="rounded-lg border-2 p-4 cursor-pointer transition-all duration-150 flex items-center gap-4"
                      style={{
                        borderColor: isSelected ? 'var(--selected-border)' : 'var(--border-color)',
                        background: isSelected ? 'var(--selected-bg)' : 'var(--background-dark)',
                      }}
                      onClick={() => setSelectedTransfer(transfer.id)}
                    >
                      <div className="text-xl font-bold text-[var(--primary-color)] min-w-[120px]">
                        {formatCurrency(transfer.amount)}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-[var(--text-primary)] mb-1">
                          {transfer.merchant_name || transfer.description}
                        </div>
                        <div className="flex gap-4 text-sm text-[var(--text-secondary)]">
                          <span>{transfer.account_name}</span>
                          <span>{formatDate(transfer.date)}</span>
                          <span className="text-[var(--warning-color)] font-medium">
                            {days} day{days !== 1 ? 's' : ''} apart
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 min-w-[140px] justify-end">
                        <span className="text-sm text-[var(--success-color)] font-medium">
                          {pct}% amount match
                        </span>
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          {isSelected
                            ? <Check className="w-5 h-5 text-[var(--primary-color)]" />
                            : null
                          }
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--border-color)]">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {potentialTransfers.length > 0 && (
            <Button
              variant="default"
              onClick={handleConfirm}
              disabled={!selectedTransfer || loading}
            >
              {loading ? 'Confirming…' : 'Confirm Transfer'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
